import { useState, useMemo } from 'react';

export type SortDirection = 'asc' | 'desc';

export interface SortConfig<T> {
  key: keyof T | string;
  direction: SortDirection;
}

export interface SortingResult<T> {
  sortedData: T[];
  sortConfig: SortConfig<T> | null;
  requestSort: (key: keyof T | string) => void;
  getSortDirection: (key: keyof T | string) => SortDirection | null;
  clearSort: () => void;
}

// Type-safe value extraction function
const getNestedValue = (obj: any, path: string): any => {
  return path.split('.').reduce((current, key) => current?.[key], obj);
};

export const useSorting = <T>(
  data: T[],
  defaultSortConfig?: SortConfig<T>
): SortingResult<T> => {
  const [sortConfig, setSortConfig] = useState<SortConfig<T> | null>(
    defaultSortConfig || null
  );

  const sortedData = useMemo(() => {
    if (!sortConfig) return data;

    return [...data].sort((a, b) => {
      const aValue = getNestedValue(a, sortConfig.key as string);
      const bValue = getNestedValue(b, sortConfig.key as string);

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      // Handle different types
      let comparison = 0;
      
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        comparison = aValue - bValue;
      } else if (typeof aValue === 'string' && typeof bValue === 'string') {
        comparison = aValue.localeCompare(bValue, undefined, { numeric: true });
      } else {
        // Convert to string for comparison
        comparison = String(aValue).localeCompare(String(bValue), undefined, { numeric: true });
      }

      return sortConfig.direction === 'desc' ? -comparison : comparison;
    });
  }, [data, sortConfig]);

  const requestSort = (key: keyof T | string) => {
    let direction: SortDirection = 'desc'; // Default to descending for financial data

    if (sortConfig && sortConfig.key === key) {
      // If clicking the same column, toggle direction
      direction = sortConfig.direction === 'desc' ? 'asc' : 'desc';
    }

    setSortConfig({ key, direction });
  };

  const getSortDirection = (key: keyof T | string): SortDirection | null => {
    if (!sortConfig || sortConfig.key !== key) {
      return null;
    }
    return sortConfig.direction;
  };

  const clearSort = () => {
    setSortConfig(null);
  };

  return {
    sortedData,
    sortConfig,
    requestSort,
    getSortDirection,
    clearSort,
  };
};